import { collection, getDocs, query, orderBy, limit, where, startAfter, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';

// 1. Visit/Live Alerts
export const fetchLiveAlertsAPI = async (serverId, categoryFilter = 'all', lastVisible = null, searchParams = null) => {
    try {
        const ref = collection(db, 'servers', serverId, 'liveNotifications');
        const validCategories = ['visit alerts', 'safety alerts', 'communications alerts', 'general alerts'];
        
        let constraints = [];

        // EXACT MATCH OVERRIDE
        if (searchParams && searchParams.value.trim() !== '') {
            constraints = [
                where(searchParams.field, '==', searchParams.value.trim()),
                orderBy('notifiedAt', 'desc'),
                limit(50)
            ];
        } else {
            // STANDARD FILTER FLOW
            if (categoryFilter === 'all') {
                constraints.push(where('category', 'in', validCategories));
            } else {
                const categoryMap = {
                    'visit': 'visit alerts',
                    'safety': 'safety alerts',
                    'communication': 'communications alerts',
                    'general': 'general alerts'
                };
                constraints.push(where('category', '==', categoryMap[categoryFilter]));
            }
            constraints.push(orderBy('notifiedAt', 'desc'), limit(50));
        }

        if (lastVisible) constraints.push(startAfter(lastVisible));

        const finalQuery = query(ref, ...constraints);
        const snapshot = await getDocs(finalQuery);
        
        const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const newLastVisible = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;

        return { data: docs, lastVisible: newLastVisible, hasMore: docs.length === 50 };
    } catch (e) {
        console.error("Error fetching live alerts:", e);
        return { data: [], lastVisible: null, hasMore: false };
    }
};

// 2. Emergency Alerts
export const fetchEmergencyAlertsAPI = async (serverId, lastVisible = null, searchParams = null) => {
    try {
        const ref = collection(db, 'servers', serverId, 'emergencyAlerts');
        let constraints = [];

        if (searchParams && searchParams.value.trim() !== '') {
            constraints = [
                where(searchParams.field, '==', searchParams.value.trim()),
                orderBy('time', 'desc'), 
                limit(50)
            ];
        } else {
            constraints = [orderBy('time', 'desc'), limit(50)];
        }

        if (lastVisible) constraints.push(startAfter(lastVisible));

        const q = query(ref, ...constraints);
        const snapshot = await getDocs(q);
        
        const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const newLastVisible = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;

        return { data: docs, lastVisible: newLastVisible, hasMore: docs.length === 50 };
    } catch (e) {
        console.error("Error fetching emergency alerts:", e);
        return { data: [], lastVisible: null, hasMore: false };
    }
};

// 3. General Notifications
export const fetchGeneralNotificationsAPI = async (serverId, lastVisible = null, searchParams = null) => {
    try {
        const ref = collection(db, 'servers', serverId, 'notifications');
        let constraints = [];

        if (searchParams && searchParams.value.trim() !== '') {
            constraints = [
                where(searchParams.field, '==', searchParams.value.trim()),
                orderBy('notifiedAt', 'desc'), 
                limit(50)
            ];
        } else {
            constraints = [orderBy('notifiedAt', 'desc'), limit(50)];
        }

        if (lastVisible) constraints.push(startAfter(lastVisible));

        const q = query(ref, ...constraints);
        const snapshot = await getDocs(q);
        
        const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const newLastVisible = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;

        return { data: docs, lastVisible: newLastVisible, hasMore: docs.length === 50 };
    } catch (e) {
        console.error("Error fetching notifications:", e);
        return { data: [], lastVisible: null, hasMore: false };
    }
};

// 4: Fetch messages for a specific emergency alert
export const fetchEmergencyMessagesAPI = async (serverId, alertId) => {
    try {
        const ref = collection(db, 'servers', serverId, 'emergencyAlerts', alertId, 'messages');
        const q = query(ref, orderBy('createdAt', 'asc')); 
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (e) {
        console.error("Error fetching emergency messages:", e);
        return [];
    }
};

// 5: Get count of messages for an alert
export const getEmergencyMessageCountsAPI = async (serverId, alertIds) => {
    const counts = {};
    await Promise.all(alertIds.map(async (alertId) => {
        try {
            const ref = collection(db, 'servers', serverId, 'emergencyAlerts', alertId, 'messages');
            const snapshot = await getCountFromServer(ref);
            counts[alertId] = snapshot.data().count;
        } catch (e) {
            console.error(`Error counting messages for ${alertId}:`, e);
            counts[alertId] = 0;
        }
    }));
    return counts;
};